import { useNavigate, useParams } from "react-router-dom";
import { services, getSolutionBySlug, servicesPageContent, aboutPageStats } from "../data/constants";
import AnimatedCounter from "../components/AnimatedCounter";
import HomeSectionHead from "../components/HomeSectionHead";
import SiteCta from "../components/SiteCta";
import ServiceSalesInquiryForm from "../components/ServiceSalesInquiryForm";

const baseUrl = process.env.PUBLIC_URL || "";

const serviceIconsBySlug = {
  "cloud-solution": "bi-cloud-arrow-up-fill",
  "storage-virtualization": "bi-hdd-stack-fill",
  "cybersecurity-solution": "bi-shield-lock-fill",
  "networking-solution": "bi-hdd-network-fill",
  "ai-iot-solutions": "bi-cpu-fill",
  "software-solutions": "bi-code-slash",
};

const tagsBySlug = Object.fromEntries(
  servicesPageContent.map((s) => [s.slug, s.tags || []])
);

function splitHeroTitle(title) {
  const ampIdx = title.indexOf(" & ");
  if (ampIdx !== -1) {
    const before = title.slice(0, ampIdx).trim();
    const after = title.slice(ampIdx).trim();
    const beforeWords = before.split(" ");
    if (beforeWords.length > 1) {
      const accent = beforeWords.pop();
      return { before: beforeWords.join(" "), accent, after };
    }
    return { before, accent: "", after };
  }
  const words = title.split(" ");
  if (words.length <= 1) return { before: title, accent: "", after: "" };
  const accent = words.pop();
  return { before: words.join(" "), accent, after: "" };
}

export default function SolutionDetailPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const solution = getSolutionBySlug(slug);

  if (!solution) {
    return (
      <div className="container py-5 text-center">
        <h2>Solution not found</h2>
        <button type="button" className="btn-primary-custom mt-3" onClick={() => navigate("/services")}>
          Back to Solutions
        </button>
      </div>
    );
  }

  const currentIndex = services.findIndex((s) => s.slug === slug);
  const prevSolution = currentIndex > 0 ? services[currentIndex - 1] : null;
  const nextSolution = currentIndex < services.length - 1 ? services[currentIndex + 1] : null;
  const relatedSolutions = services.filter((s) => s.slug !== slug).slice(0, 3);

  const introParagraphs = solution.introParagraphs || [solution.longDesc];
  const overviewParagraphs = introParagraphs.filter(
    (p, i) => i > 0 || p !== (solution.subtitle || solution.desc)
  );
  const displayParagraphs = overviewParagraphs.length > 0 ? overviewParagraphs : introParagraphs;

  const heroTitle = splitHeroTitle(solution.title);
  const heroIcon = serviceIconsBySlug[solution.slug] || solution.icon;
  const tags = tagsBySlug[solution.slug] || [];
  const featureHighlights = (solution.features || []).slice(0, 4).map((f) => f.title);

  return (
    <div className="solution-detail-page">
      <section className="solution-detail-hero animate-on-scroll">
        <div className="container">
          <nav className="solution-detail-breadcrumb" aria-label="Breadcrumb">
            <button type="button" onClick={() => navigate("/services")}>Services</button>
            <i className="bi bi-chevron-right" aria-hidden="true" />
            <span>{solution.title}</span>
          </nav>

          <div className="row g-4 g-lg-5 align-items-center">
            <div className="col-lg-7">
              <span className="solution-detail-label">Service Detail</span>
              <h1 className="solution-detail-hero-title">
                {heroTitle.before}
                {heroTitle.accent ? (
                  <>
                    {" "}
                    <span className="solution-detail-hero-accent">{heroTitle.accent}</span>
                  </>
                ) : null}
                {heroTitle.after ? ` ${heroTitle.after}` : ""}
              </h1>
              <p className="solution-detail-hero-desc">
                {solution.subtitle || solution.desc}
              </p>

              {tags.length > 0 && (
                <div className="solution-detail-hero-tags">
                  {tags.map((tag) => (
                    <span key={tag} className="solution-detail-hero-tag">{tag}</span>
                  ))}
                </div>
              )}

              <div className="solution-detail-hero-actions">
                <button
                  type="button"
                  className="solution-detail-hero-btn-primary"
                  onClick={() => {
                    document.getElementById("service-inquiry")?.scrollIntoView({ behavior: "smooth" });
                  }}
                >
                  Request a Quote <i className="bi bi-arrow-right ms-1" aria-hidden="true" />
                </button>
                <button type="button" className="solution-detail-hero-btn-outline" onClick={() => navigate("/contact")}>
                  General Contact
                </button>
              </div>
            </div>

            <div className="col-lg-5">
              <div className="solution-detail-hero-visual">
                {solution.image ? (
                  <>
                    <img
                      src={`${baseUrl}/img/services/${encodeURIComponent(solution.image)}`}
                      alt={solution.title}
                      className="solution-detail-hero-img"
                      loading="eager"
                      onError={(e) => {
                        e.target.style.display = "none";
                        e.target.nextElementSibling?.classList.remove("d-none");
                      }}
                    />
                    <div className="solution-detail-hero-img-fallback d-none">
                      <i className={`bi ${heroIcon}`} />
                    </div>
                  </>
                ) : (
                  <div className="solution-detail-hero-img-fallback">
                    <i className={`bi ${heroIcon}`} />
                  </div>
                )}
                <div className="solution-detail-hero-float">
                  <div className="solution-detail-hero-float-icon">
                    <i className={`bi ${heroIcon}`} />
                  </div>
                  <div>
                    <div className="solution-detail-hero-float-title">{solution.title}</div>
                    <div className="solution-detail-hero-float-meta">
                      {solution.features?.length || 0}+ capabilities
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="home-stats-section solution-detail-stats animate-on-scroll">
        <div className="container">
          <div className="home-stats-grid">
            {aboutPageStats.map((item) => (
              <div key={item.label} className="home-stat-card">
                <AnimatedCounter
                  value={item.value}
                  suffix={item.suffix}
                  className="home-stat-num"
                  as="div"
                />
                <div className="home-stat-label">{item.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="section solution-detail-overview animate-on-scroll">
        <div className="container">
          <div className="home-panel-card">
            <div className="row g-4 g-lg-5 align-items-center">
              <div className="col-lg-5">
                <div className="solution-detail-overview-aside">
                  <div className="solution-detail-overview-icon">
                    <i className={`bi ${heroIcon}`} />
                  </div>
                  <h2 className="solution-detail-overview-title">What We Offer</h2>
                  <p className="solution-detail-overview-lead">
                    End-to-end expertise tailored to your business outcomes.
                  </p>
                  {featureHighlights.length > 0 && (
                    <ul className="solution-detail-highlights">
                      {featureHighlights.map((item) => (
                        <li key={item}>
                          <i className="bi bi-check-circle-fill" aria-hidden="true" />
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>
              <div className="col-lg-7">
                <div className="solution-detail-text">
                  {displayParagraphs.map((p, i) => (
                    <p key={i}>{p}</p>
                  ))}
                </div>
                <div className="solution-detail-nav-links">
                  {prevSolution ? (
                    <button
                      type="button"
                      className="solution-detail-nav-link"
                      onClick={() => navigate(`/solutions/${prevSolution.slug}`)}
                    >
                      <i className="bi bi-arrow-left" aria-hidden="true" />
                      <span>
                        <small>Previous</small>
                        {prevSolution.title}
                      </span>
                    </button>
                  ) : <span />}
                  {nextSolution ? (
                    <button
                      type="button"
                      className="solution-detail-nav-link solution-detail-nav-link--next"
                      onClick={() => navigate(`/solutions/${nextSolution.slug}`)}
                    >
                      <span>
                        <small>Next</small>
                        {nextSolution.title}
                      </span>
                      <i className="bi bi-arrow-right" aria-hidden="true" />
                    </button>
                  ) : null}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {solution.features && solution.features.length > 0 && (
        <section className="section solution-detail-features animate-on-scroll">
          <div className="container">
            <HomeSectionHead
              label="Key Capabilities"
              title="What We Deliver"
              desc={`Core offerings and expertise within ${solution.title.toLowerCase()}.`}
            />
            <div className="row g-4 solution-detail-features-grid">
              {solution.features.map((f, i) => (
                <div key={f.title} className="col-lg-4 col-md-6">
                  <article className="solution-detail-feature-card h-100">
                    <div className="solution-detail-feature-top">
                      <span className="solution-detail-feature-num">{String(i + 1).padStart(2, "0")}</span>
                      <div className="solution-detail-feature-icon">
                        <i className={`bi ${f.icon}`} />
                      </div>
                    </div>
                    <h3>{f.title}</h3>
                    <p>{f.desc}</p>
                  </article>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {solution.commitmentTitle && solution.commitmentItems && solution.commitmentItems.length > 0 && (
        <section className="section solution-detail-commitment animate-on-scroll">
          <div className="container">
            <HomeSectionHead
              label="Our Promise"
              title={solution.commitmentTitle}
              desc="How we ensure reliable delivery, security, and long-term value for every engagement."
            />
            <div className="solution-detail-commitment-grid">
              {solution.commitmentItems.map((item) => (
                <article key={item.title} className="solution-detail-commitment-card">
                  <div className="solution-detail-commitment-icon">
                    <i className="bi bi-check2" />
                  </div>
                  <h3>{item.title}</h3>
                  <p>{item.desc}</p>
                </article>
              ))}
            </div>
          </div>
        </section>
      )}

      <section className="section solution-detail-related animate-on-scroll">
        <div className="container">
          <HomeSectionHead
            label="Explore More"
            title="Related Services"
            desc="Discover additional areas across our solution stack."
          />
          <div className="row g-4">
            {relatedSolutions.map((s) => (
              <div key={s.slug} className="col-md-4">
                <article
                  className="services-page-card"
                  onClick={() => navigate(`/solutions/${s.slug}`)}
                  onKeyDown={(e) => e.key === "Enter" && navigate(`/solutions/${s.slug}`)}
                  role="link"
                  tabIndex={0}
                  aria-label={`View ${s.title} solution`}
                >
                  <div className="services-page-card-top">
                    <div className="services-page-card-icon">
                      <i className={`bi ${serviceIconsBySlug[s.slug] || "bi-gear-wide-connected"}`} />
                    </div>
                    <span className="services-page-card-arrow" aria-hidden="true">
                      <i className="bi bi-arrow-up-right" />
                    </span>
                  </div>
                  <h5 className="services-page-card-title">{s.title}</h5>
                  <p className="services-page-card-desc">{s.desc}</p>
                  {tagsBySlug[s.slug]?.length > 0 && (
                    <div className="services-page-card-tags">
                      {tagsBySlug[s.slug].slice(0, 2).map((tag) => (
                        <span key={tag} className="services-page-card-tag">{tag}</span>
                      ))}
                    </div>
                  )}
                </article>
              </div>
            ))}
          </div>
          <div className="solution-detail-related-cta">
            <button type="button" className="solution-detail-hero-btn-outline" onClick={() => navigate("/services")}>
              View All Services <i className="bi bi-grid ms-1" aria-hidden="true" />
            </button>
          </div>
        </div>
      </section>

      <ServiceSalesInquiryForm serviceTitle={solution.title} serviceSlug={solution.slug} />

      <SiteCta
        variant="card"
        badge="Get Started"
        title="Ready to transform your"
        accent="IT infrastructure?"
        desc="Partner with Emicron for end-to-end solutions — from assessment and deployment to 24×7 managed operations. Let's discuss what your business needs next."
        buttonText="Contact Us"
        to="/contact"
        secondaryButtonText="Explore Solutions"
        secondaryTo="/services"
      />
    </div>
  );
}
